import React, { useState, useEffect, useCallback } from 'react';
import {
  Star, Users, Save, X, Edit3, RefreshCw, Loader2, Mail, Eye, EyeOff,
  Plus, Trash2, ImagePlus, AlertTriangle, Trophy, Target, Quote, ShieldCheck,
  Ban, Linkedin, MessageCircle, Instagram, Music, Twitter, Github, Globe,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { staffMembers as BASE_STAFF } from '../../mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Static base data for execs — fallback when no DB override exists.
const BASE_EXECS = [
  { id: 1, name: 'Bella T.',  role: 'Chief Executive Officer',     role_short: 'CEO',  avatar: 'https://customer-assets-39nsmqrw.emergentagent.net/job_209c8813-566e-47da-85c4-1d1fadce8833/artifacts/dg6zknpv_image.png' },
  { id: 2, name: 'Kayla',     role: 'Chief Financial Officer',     role_short: 'CFO',  avatar: 'https://customer-assets.emergentagent.com/job_starnight-group/artifacts/n4aaqshe_valindraa.webp' },
  { id: 3, name: 'Denis',     role: 'Chief Operating Officer',     role_short: 'COO',  avatar: 'https://customer-assets.emergentagent.com/job_starnight-group/artifacts/1lot5hem_denisp101.webp' },
];

const TAG_COLORS = [
  { value: 'cyan',   label: 'Cyan',   cls: 'bg-[#111a1c] text-[#00E5FF] border-[#00E5FF]/25' },
  { value: 'violet', label: 'Violet', cls: 'bg-[#1a1220] text-[#c39be5] border-[#8A2BE2]/25' },
  { value: 'red',    label: 'Red',    cls: 'bg-[#1a1212] text-red-300 border-red-500/25' },
  { value: 'amber',  label: 'Amber',  cls: 'bg-[#1a1712] text-amber-300 border-amber-500/25' },
];

const SOCIAL_ICONS = { linkedin: Linkedin, discord: MessageCircle, instagram: Instagram, spotify: Music, twitter: Twitter, github: Github, website: Globe, roblox: Globe };

const INPUT_CLS = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/30 transition-colors";

const TeamManagementPage = ({ getAuthHeaders, currentUser }) => {
  const isSuperadmin = currentUser?.role === 'superadmin';
  const [tab, setTab] = useState('executives'); // 'executives' | 'staff'
  const [execOverrides, setExecOverrides] = useState({});
  const [staffOverrides, setStaffOverrides] = useState({});
  const [staffProfiles, setStaffProfiles] = useState({}); // admin_users data for real socials/avatars
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { kind: 'exec'|'staff', data }
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [execRes, staffRes, profileRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/executive-profiles`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/staff-profile-overrides`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/staff-profiles`).catch(() => ({ data: [] })),
      ]);
      const em = {}; (execRes.data || []).forEach(p => { if (p.exec_id) em[p.exec_id] = p; });
      const sm = {}; (staffRes.data || []).forEach(p => { if (p.staff_id) sm[p.staff_id] = p; });
      const pm = {}; (profileRes.data || []).forEach(p => { if (p.username) pm[p.username.toLowerCase()] = p; });
      setExecOverrides(em);
      setStaffOverrides(sm);
      setStaffProfiles(pm);
    } catch (err) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (isSuperadmin) fetchAll(); }, [isSuperadmin, fetchAll]);

  // ─── Executives merge ───
  const execs = [
    ...BASE_EXECS.map(b => {
      const o = execOverrides[b.id] || {};
      return {
        id: b.id, isCustom: false,
        name: o.name ?? b.name,
        role: o.role ?? b.role,
        role_short: o.role_short ?? b.role_short,
        avatar: o.avatar ?? b.avatar,
        bio: o.bio ?? '',
        full_bio: o.full_bio ?? '',
        focus_areas: o.focus_areas ?? [],
        joined_year: o.joined_year ?? null,
        achievements: o.achievements ?? [],
        quote: o.quote ?? '',
        contact_email: o.contact_email ?? '',
        show_email: !!o.show_email,
        hidden: !!o.hidden,
        hasOverride: !!execOverrides[b.id],
        updated_at: o.updated_at,
        updated_by: o.updated_by,
      };
    }),
    ...Object.values(execOverrides).filter(o => o.exec_id > 100 && o.is_custom).map(o => ({
      id: o.exec_id, isCustom: true,
      name: o.name || 'Unnamed', role: o.role || '', role_short: o.role_short || '',
      avatar: o.avatar || '', bio: o.bio || '', full_bio: o.full_bio || '',
      focus_areas: o.focus_areas || [], joined_year: o.joined_year,
      achievements: o.achievements || [], quote: o.quote || '',
      contact_email: o.contact_email || '', show_email: !!o.show_email,
      hidden: !!o.hidden, hasOverride: true,
      updated_at: o.updated_at, updated_by: o.updated_by,
    })),
  ];

  // ─── Staff merge ───
  const findProfile = (name) => {
    const key = (name || '').toLowerCase();
    return Object.values(staffProfiles).find(p =>
      p.display_name?.toLowerCase() === key || p.username?.toLowerCase() === key.replace(/\s+/g, '')
    );
  };
  const staff = [
    ...BASE_STAFF.map(b => {
      const o = staffOverrides[b.id] || {};
      const p = findProfile(b.name) || {};
      return {
        id: b.id, isCustom: false, initials: b.initials,
        name: o.name ?? b.name,
        role: o.role ?? b.role,
        bio: o.bio ?? p.bio ?? b.bio,
        avatar: o.avatar ?? p.avatar ?? null,
        contact_email: o.contact_email ?? p.contact_email ?? '',
        socials: o.socials ?? p.socials ?? {},
        tags: (o.tags && o.tags.length) ? o.tags : (b.tags || []),
        hidden: !!o.hidden,
        hasOverride: !!staffOverrides[b.id],
        hasLiveProfile: !!p.username,
        updated_at: o.updated_at,
        updated_by: o.updated_by,
      };
    }),
    ...Object.values(staffOverrides).filter(o => o.staff_id > 100 && o.is_custom).map(o => ({
      id: o.staff_id, isCustom: true,
      initials: o.initials || (o.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
      name: o.name || 'Unnamed',
      role: o.role || '',
      bio: o.bio || '',
      avatar: o.avatar || '',
      contact_email: o.contact_email || '',
      socials: o.socials || {},
      tags: o.tags || [],
      hidden: !!o.hidden,
      hasOverride: true,
      hasLiveProfile: false,
      updated_at: o.updated_at,
      updated_by: o.updated_by,
    })),
  ];

  // ─── Actions ───
  const addNewExec = () => {
    const existing = Object.values(execOverrides).filter(o => o.exec_id > 100 && o.is_custom).map(o => o.exec_id);
    const nextId = existing.length ? Math.max(...existing) + 1 : 101;
    setEditing({ kind: 'exec', data: {
      id: nextId, isCustom: true, name: 'New Executive', role: 'Chief Officer', role_short: 'CxO',
      avatar: '', bio: '', full_bio: '', focus_areas: [], joined_year: new Date().getFullYear(),
      achievements: [], quote: '', contact_email: '', show_email: false, hidden: false,
    }});
  };

  const addNewStaff = () => {
    const existing = Object.values(staffOverrides).filter(o => o.staff_id > 100 && o.is_custom).map(o => o.staff_id);
    const nextId = existing.length ? Math.max(...existing) + 1 : 101;
    setEditing({ kind: 'staff', data: {
      id: nextId, isCustom: true, name: 'New Staff', role: 'Team Member',
      avatar: '', initials: 'NS', bio: '', contact_email: '', socials: {}, tags: [], hidden: false,
    }});
  };

  const saveExec = async (form) => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        name: form.name, role: form.role, role_short: form.role_short, avatar: form.avatar,
        bio: form.bio, full_bio: form.full_bio,
        focus_areas: (form.focus_areas || []).filter(Boolean),
        joined_year: form.joined_year ? Number(form.joined_year) : null,
        achievements: (form.achievements || []).filter(Boolean),
        quote: form.quote, contact_email: form.contact_email,
        show_email: form.show_email, hidden: form.hidden,
        is_custom: form.isCustom,
      };
      await axios.put(`${BACKEND_URL}/api/admin/executive-profiles/${form.id}`, payload, { headers });
      toast.success(`${form.name} updated`);
      setEditing(null); fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveStaff = async (form) => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        name: form.name, role: form.role, bio: form.bio,
        avatar: form.avatar || '', initials: form.initials || '',
        contact_email: form.contact_email || '',
        socials: form.socials || {},
        tags: (form.tags || []).filter(t => t.label && t.label.trim()),
        hidden: !!form.hidden,
        is_custom: !!form.isCustom,
      };
      await axios.put(`${BACKEND_URL}/api/admin/staff-profile-overrides/${form.id}`, payload, { headers });
      toast.success(`${form.name} updated`);
      setEditing(null); fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const resetExec = async (id) => {
    if (!window.confirm('Reset to default? This removes the custom override.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/executive-profiles/${id}`, { headers: getAuthHeaders() });
      toast.success('Reset to defaults'); fetchAll();
    } catch { toast.error('Reset failed'); }
  };

  const resetStaff = async (id) => {
    if (!window.confirm('Reset to default? This removes the custom override.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/staff-profile-overrides/${id}`, { headers: getAuthHeaders() });
      toast.success('Reset to defaults'); fetchAll();
    } catch { toast.error('Reset failed'); }
  };

  // Delete = remove from public /team. For custom entries, fully removes them.
  // For base entries, hides them (they can be Restored later).
  const deleteExec = async (exec) => {
    const confirmMsg = exec.isCustom
      ? `Permanently delete ${exec.name}? This cannot be undone.`
      : `Remove ${exec.name} from the public /team page? You can Restore them anytime.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const headers = getAuthHeaders();
      if (exec.isCustom) {
        await axios.delete(`${BACKEND_URL}/api/admin/executive-profiles/${exec.id}`, { headers });
        toast.success(`${exec.name} deleted`);
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/executive-profiles/${exec.id}`, { hidden: true }, { headers });
        toast.success(`${exec.name} removed from /team`);
      }
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const deleteStaff = async (staff) => {
    const confirmMsg = staff.isCustom
      ? `Permanently delete ${staff.name}? This cannot be undone.`
      : `Remove ${staff.name} from the public /team page? You can Restore them anytime.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const headers = getAuthHeaders();
      if (staff.isCustom) {
        await axios.delete(`${BACKEND_URL}/api/admin/staff-profile-overrides/${staff.id}`, { headers });
        toast.success(`${staff.name} deleted`);
      } else {
        await axios.put(`${BACKEND_URL}/api/admin/staff-profile-overrides/${staff.id}`, { hidden: true }, { headers });
        toast.success(`${staff.name} removed from /team`);
      }
      fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const restoreExec = async (id, name) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/executive-profiles/${id}`, { hidden: false }, { headers: getAuthHeaders() });
      toast.success(`${name} restored`);
      fetchAll();
    } catch { toast.error('Restore failed'); }
  };

  const restoreStaff = async (id, name) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/staff-profile-overrides/${id}`, { hidden: false }, { headers: getAuthHeaders() });
      toast.success(`${name} restored`);
      fetchAll();
    } catch { toast.error('Restore failed'); }
  };

  const clearStaffSocials = async (id, name) => {
    if (!window.confirm(`Force-clear ALL social links for ${name}? This overrides whatever they set in their own profile.`)) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/staff-profile-overrides/${id}`, { clear_socials: true }, { headers: getAuthHeaders() });
      toast.success(`Cleared connections for ${name}`); fetchAll();
    } catch { toast.error('Clear failed'); }
  };

  // ─── Gate ───
  if (!isSuperadmin) {
    return (
      <div className="flex items-center justify-center h-[70vh]" data-testid="team-mgmt-denied">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-light text-white tracking-tight mb-1">Restricted</h2>
          <p className="text-white/40 text-sm">Only superadmins can manage the public Team page.</p>
        </div>
      </div>
    );
  }

  const activeList = tab === 'executives' ? execs : staff;
  const visibleCount = activeList.filter(x => !x.hidden).length;

  return (
    <div className="space-y-6 pb-8" data-testid="team-mgmt-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12] p-6">
        <div className="pointer-events-none absolute top-0 right-0 w-[400px] h-[200px] bg-[#00E5FF]/[0.04] rounded-full blur-[120px] -translate-y-1/4 translate-x-1/4" />
        <div className="pointer-events-none absolute bottom-0 left-0 w-[300px] h-[180px] bg-[#8A2BE2]/[0.04] rounded-full blur-[100px] translate-y-1/4 -translate-x-1/4" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00E5FF]/[0.15] to-[#8A2BE2]/[0.15] border border-white/10 flex items-center justify-center"
              style={{ boxShadow: '0 0 30px rgba(0,229,255,0.1)' }}>
              <Users className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Public site · /team</p>
              <h1 className="text-2xl font-light text-white tracking-tighter">Team Page <span className="bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] bg-clip-text text-transparent font-medium">Manager</span></h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab === 'executives' ? (
              <Button onClick={addNewExec} data-testid="add-exec-btn"
                className="h-10 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase font-medium bg-[#00E5FF]/[0.08] text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/[0.15]">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Executive
              </Button>
            ) : (
              <Button onClick={addNewStaff} data-testid="add-staff-btn"
                className="h-10 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase font-medium bg-[#00E5FF]/[0.08] text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/[0.15]">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Staff
              </Button>
            )}
            <Button onClick={fetchAll} variant="ghost" size="sm" disabled={loading} data-testid="refresh-team-mgmt"
              className="h-9 w-9 p-0 rounded-full text-white/40 hover:text-white">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="relative mt-6 flex items-center gap-2 pt-4 border-t border-white/[0.06]">
          <TabBtn active={tab === 'executives'} onClick={() => setTab('executives')} testid="tab-executives"
            icon={<Star className="w-3.5 h-3.5" />} label="Executives" count={execs.filter(e => !e.hidden).length} />
          <TabBtn active={tab === 'staff'} onClick={() => setTab('staff')} testid="tab-staff"
            icon={<Users className="w-3.5 h-3.5" />} label="Core Staff" count={staff.filter(s => !s.hidden).length} />
          <span className="ml-auto text-[10px] tracking-[0.15em] uppercase text-white/30">
            {visibleCount} live · {activeList.length - visibleCount} hidden
          </span>
        </div>
      </div>

      {loading && !Object.keys(execOverrides).length && !Object.keys(staffOverrides).length ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid={`team-mgmt-grid-${tab}`}>
          {tab === 'executives' && execs.map(e => (
            <ExecCard key={e.id} exec={e}
              onEdit={() => setEditing({ kind: 'exec', data: e })}
              onReset={() => resetExec(e.id)}
              onDelete={() => deleteExec(e)}
              onRestore={() => restoreExec(e.id, e.name)}
            />
          ))}
          {tab === 'staff' && staff.map(s => (
            <StaffCard key={s.id} staff={s}
              onEdit={() => setEditing({ kind: 'staff', data: s })}
              onReset={() => resetStaff(s.id)}
              onDelete={() => deleteStaff(s)}
              onRestore={() => restoreStaff(s.id, s.name)}
              onClearSocials={() => clearStaffSocials(s.id, s.name)}
            />
          ))}
        </div>
      )}

      {editing && editing.kind === 'exec' && (
        <EditExecModal exec={editing.data} onClose={() => setEditing(null)} onSave={saveExec} saving={saving} />
      )}
      {editing && editing.kind === 'staff' && (
        <EditStaffModal staff={editing.data} onClose={() => setEditing(null)} onSave={saveStaff} saving={saving} />
      )}
    </div>
  );
};

// ─── Tab button ───
const TabBtn = ({ active, onClick, icon, label, count, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.1em] uppercase font-medium border transition-all ${
      active
        ? 'bg-[#00E5FF]/[0.1] text-[#00E5FF] border-[#00E5FF]/30'
        : 'bg-white/[0.02] text-white/50 border-white/[0.06] hover:text-white/80 hover:border-white/[0.12]'
    }`}>
    {icon} {label}
    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] tabular-nums ${active ? 'bg-[#00E5FF]/[0.15] text-[#00E5FF]' : 'bg-white/[0.06] text-white/40'}`}>{count}</span>
  </button>
);

// ─── Exec preview card ───
const ExecCard = ({ exec, onEdit, onReset, onDelete, onRestore }) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-5 hover:border-white/[0.12] transition-all" data-testid={`mgmt-exec-card-${exec.id}`}>
    <div className="flex items-start gap-4">
      <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-[#0A0A12] flex-shrink-0">
        {exec.avatar ? (
          <img src={exec.avatar} alt={exec.name} className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/15 text-xl font-light">{exec.name.charAt(0)}</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <h3 className="text-white font-medium tracking-tight truncate">{exec.name}</h3>
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#00E5FF]/70 truncate">{exec.role_short} · {exec.role}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {exec.hasOverride && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.12em] uppercase bg-emerald-500/10 text-emerald-300 border border-emerald-500/25">
                {exec.isCustom ? 'Custom' : 'Customised'}
              </span>
            )}
            {exec.hidden && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.12em] uppercase bg-red-500/10 text-red-300 border border-red-500/25">Hidden</span>
            )}
          </div>
        </div>
        <p className="text-white/40 text-xs leading-relaxed line-clamp-2 mb-2">{exec.bio || exec.full_bio || 'No bio set — using default.'}</p>
        {exec.contact_email && (
          <p className={`text-[10px] flex items-center gap-1.5 ${exec.show_email ? 'text-[#00E5FF]/70' : 'text-white/30'}`}>
            {exec.show_email ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}
            {exec.show_email ? `Public: ${exec.contact_email}` : `Hidden: ${exec.contact_email}`}
          </p>
        )}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <Button size="sm" onClick={onEdit} data-testid={`edit-exec-${exec.id}`}
            className="h-8 px-3.5 rounded-full text-[10px] tracking-[0.1em] uppercase bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/15">
            <Edit3 className="w-3 h-3 mr-1.5" /> Edit
          </Button>
          {exec.hidden ? (
            <Button size="sm" variant="ghost" onClick={onRestore} data-testid={`restore-exec-${exec.id}`}
              className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-emerald-300/70 hover:text-emerald-300 bg-emerald-500/[0.05] hover:bg-emerald-500/[0.1] border border-emerald-500/20">
              <Eye className="w-3 h-3 mr-1.5" /> Restore
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`delete-exec-${exec.id}`}
              className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-red-300/70 hover:text-red-300 bg-red-500/[0.05] hover:bg-red-500/[0.1] border border-red-500/20">
              <Trash2 className="w-3 h-3 mr-1.5" /> {exec.isCustom ? 'Delete' : 'Remove'}
            </Button>
          )}
          {exec.hasOverride && !exec.isCustom && (
            <Button size="sm" variant="ghost" onClick={onReset} data-testid={`reset-exec-${exec.id}`}
              className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-white/40 hover:text-white/70 hover:bg-white/[0.05]">
              <RefreshCw className="w-3 h-3 mr-1.5" /> Reset
            </Button>
          )}
        </div>
      </div>
    </div>
    {exec.updated_by && (
      <p className="text-[9px] text-white/25 mt-3 pt-3 border-t border-white/[0.04]">
        Updated by {exec.updated_by} · {exec.updated_at ? new Date(exec.updated_at).toLocaleDateString() : ''}
      </p>
    )}
  </div>
);

// ─── Staff preview card ───
const StaffCard = ({ staff, onEdit, onReset, onDelete, onRestore, onClearSocials }) => {
  const socialsCount = staff.socials ? Object.values(staff.socials).filter(v => v && String(v).trim()).length : 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0A0A12]/60 backdrop-blur-md p-5 hover:border-white/[0.12] transition-all" data-testid={`mgmt-staff-card-${staff.id}`}>
      <div className="flex items-start gap-4">
        <div className="relative w-16 h-16 rounded-full overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.04] to-[#0A0A12] flex-shrink-0 flex items-center justify-center">
          {staff.avatar ? (
            <img src={staff.avatar} alt={staff.name} className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <span className="text-white/25 font-light text-lg">{staff.initials || staff.name.charAt(0)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0">
              <h3 className="text-white font-medium tracking-tight truncate">{staff.name}</h3>
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#00E5FF]/70 truncate">{staff.role}</p>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              {staff.hasOverride && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.12em] uppercase bg-emerald-500/10 text-emerald-300 border border-emerald-500/25">
                  {staff.isCustom ? 'Custom' : 'Customised'}
                </span>
              )}
              {staff.hidden && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.12em] uppercase bg-red-500/10 text-red-300 border border-red-500/25">Hidden</span>
              )}
              {staff.hasLiveProfile && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] tracking-[0.12em] uppercase bg-white/[0.04] text-white/50 border border-white/10">Has account</span>
              )}
            </div>
          </div>
          <p className="text-white/40 text-xs leading-relaxed line-clamp-2 mb-2">{staff.bio || 'No bio set.'}</p>

          {staff.tags && staff.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {staff.tags.map((t, i) => {
                const style = TAG_COLORS.find(c => c.value === t.color) || TAG_COLORS[0];
                return (
                  <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] tracking-[0.08em] font-medium border ${style.cls}`}>
                    <ShieldCheck className="w-2.5 h-2.5" strokeWidth={2} /> {t.label}
                  </span>
                );
              })}
            </div>
          )}

          {socialsCount > 0 && (
            <p className="text-[10px] text-white/40 flex items-center gap-1.5 mb-2">
              <Globe className="w-2.5 h-2.5" /> {socialsCount} social{socialsCount === 1 ? '' : 's'} connected
            </p>
          )}

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Button size="sm" onClick={onEdit} data-testid={`edit-staff-${staff.id}`}
              className="h-8 px-3.5 rounded-full text-[10px] tracking-[0.1em] uppercase bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/25 hover:bg-[#00E5FF]/15">
              <Edit3 className="w-3 h-3 mr-1.5" /> Edit
            </Button>
            {staff.hidden ? (
              <Button size="sm" variant="ghost" onClick={onRestore} data-testid={`restore-staff-${staff.id}`}
                className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-emerald-300/70 hover:text-emerald-300 bg-emerald-500/[0.05] hover:bg-emerald-500/[0.1] border border-emerald-500/20">
                <Eye className="w-3 h-3 mr-1.5" /> Restore
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`delete-staff-${staff.id}`}
                className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-red-300/70 hover:text-red-300 bg-red-500/[0.05] hover:bg-red-500/[0.1] border border-red-500/20">
                <Trash2 className="w-3 h-3 mr-1.5" /> {staff.isCustom ? 'Delete' : 'Remove'}
              </Button>
            )}
            {socialsCount > 0 && (
              <Button size="sm" variant="ghost" onClick={onClearSocials} data-testid={`clear-socials-${staff.id}`}
                className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-white/40 hover:text-amber-300 hover:bg-amber-500/[0.05]">
                <Ban className="w-3 h-3 mr-1.5" /> Clear socials
              </Button>
            )}
            {staff.hasOverride && !staff.isCustom && (
              <Button size="sm" variant="ghost" onClick={onReset} data-testid={`reset-staff-${staff.id}`}
                className="h-8 px-3 rounded-full text-[10px] tracking-[0.1em] uppercase text-white/40 hover:text-white/70 hover:bg-white/[0.05]">
                <RefreshCw className="w-3 h-3 mr-1.5" /> Reset
              </Button>
            )}
          </div>
        </div>
      </div>
      {staff.updated_by && (
        <p className="text-[9px] text-white/25 mt-3 pt-3 border-t border-white/[0.04]">
          Updated by {staff.updated_by} · {staff.updated_at ? new Date(staff.updated_at).toLocaleDateString() : ''}
        </p>
      )}
    </div>
  );
};

// ─── Executive edit modal ───
const EditExecModal = ({ exec, onClose, onSave, saving, getAuthHeaders }) => {
  const [form, setForm] = useState({
    ...exec,
    focus_areas: exec.focus_areas?.length ? exec.focus_areas : [''],
    achievements: exec.achievements?.length ? exec.achievements : [''],
  });
  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateArr = (key, idx, val) => setForm(f => ({ ...f, [key]: f[key].map((x, i) => i === idx ? val : x) }));
  const addArr = (key) => setForm(f => ({ ...f, [key]: [...f[key], ''] }));
  const removeArr = (key, idx) => setForm(f => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  return (
    <ModalShell title="Executive" name={exec.name} onClose={onClose}>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Display Name"><input value={form.name || ''} onChange={(e) => updateField('name', e.target.value)} className={INPUT_CLS} data-testid="edit-exec-name" /></Field>
        <Field label="Short Role"><input value={form.role_short || ''} onChange={(e) => updateField('role_short', e.target.value)} className={INPUT_CLS} placeholder="CEO / CFO / COO …" /></Field>
      </div>
      <Field label="Full Role"><input value={form.role || ''} onChange={(e) => updateField('role', e.target.value)} className={INPUT_CLS} data-testid="edit-exec-role" /></Field>

      <AvatarField value={form.avatar} onChange={(v) => updateField('avatar', v)} />

      <Field label="Short Bio" help="One-line summary on the card (max ~140 chars).">
        <input value={form.bio || ''} onChange={(e) => updateField('bio', e.target.value)} className={INPUT_CLS} maxLength={140} />
      </Field>
      <Field label="Full Bio" help="Longer paragraph shown in the profile modal.">
        <textarea value={form.full_bio || ''} onChange={(e) => updateField('full_bio', e.target.value)} rows={4} className={`${INPUT_CLS} resize-none`} />
      </Field>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Joined Year"><input type="number" value={form.joined_year || ''} onChange={(e) => updateField('joined_year', e.target.value)} className={INPUT_CLS} placeholder="2023" min="2000" max="2099" /></Field>
        <Field label={<span className="flex items-center gap-1.5"><Quote className="w-3 h-3" /> Personal Quote</span>}>
          <input value={form.quote || ''} onChange={(e) => updateField('quote', e.target.value)} className={INPUT_CLS} placeholder="A few words…" />
        </Field>
      </div>

      <ArrayField label={<span className="flex items-center gap-1.5"><Target className="w-3 h-3" /> Focus Areas</span>}
        items={form.focus_areas} placeholder="e.g. Strategic vision"
        onChange={(idx, val) => updateArr('focus_areas', idx, val)}
        onAdd={() => addArr('focus_areas')} onRemove={(idx) => removeArr('focus_areas', idx)} />

      <ArrayField label={<span className="flex items-center gap-1.5"><Trophy className="w-3 h-3" /> Notable Wins</span>}
        items={form.achievements} placeholder="e.g. Launched the Staff Hub"
        onChange={(idx, val) => updateArr('achievements', idx, val)}
        onAdd={() => addArr('achievements')} onRemove={(idx) => removeArr('achievements', idx)} />

      <VisibilityToggle hidden={!!form.hidden} onToggle={() => updateField('hidden', !form.hidden)} />
      <EmailField email={form.contact_email} show={form.show_email}
        onEmail={(v) => updateField('contact_email', v)} onShow={() => updateField('show_email', !form.show_email)} />

      <ModalFooter onCancel={onClose} onSave={() => onSave(form)} saving={saving} testid="save-exec-btn" />
    </ModalShell>
  );
};

// ─── Staff edit modal ───
const EditStaffModal = ({ staff, onClose, onSave, saving, getAuthHeaders }) => {
  const [form, setForm] = useState({
    ...staff,
    socials: { linkedin: '', discord: '', instagram: '', spotify: '', twitter: '', github: '', website: '', roblox: '', ...(staff.socials || {}) },
    tags: staff.tags?.length ? staff.tags.map(t => ({ ...t })) : [],
  });
  const updateField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateSocial = (k, v) => setForm(f => ({ ...f, socials: { ...f.socials, [k]: v } }));
  const updateTag = (idx, key, val) => setForm(f => ({ ...f, tags: f.tags.map((t, i) => i === idx ? { ...t, [key]: val } : t) }));
  const addTag = () => setForm(f => ({ ...f, tags: [...f.tags, { label: '', color: 'cyan' }] }));
  const removeTag = (idx) => setForm(f => ({ ...f, tags: f.tags.filter((_, i) => i !== idx) }));

  return (
    <ModalShell title="Staff member" name={staff.name} onClose={onClose}>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Display Name"><input value={form.name || ''} onChange={(e) => updateField('name', e.target.value)} className={INPUT_CLS} data-testid="edit-staff-name" /></Field>
        <Field label="Job Title / Role" help="Only affects the /team card. Does NOT change portal permissions.">
          <input value={form.role || ''} onChange={(e) => updateField('role', e.target.value)} className={INPUT_CLS} data-testid="edit-staff-role" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Initials (fallback)" help="Shown if no avatar is set.">
          <input value={form.initials || ''} onChange={(e) => updateField('initials', e.target.value.toUpperCase().slice(0, 3))} className={INPUT_CLS} placeholder="MW" maxLength={3} />
        </Field>
        <Field label="Contact Email">
          <input value={form.contact_email || ''} onChange={(e) => updateField('contact_email', e.target.value)} className={INPUT_CLS} placeholder="name@starnightholdings.uk" type="email" />
        </Field>
      </div>

      <AvatarField value={form.avatar} onChange={(v) => updateField('avatar', v)}
        staffId={form.id}
        getAuthHeaders={getAuthHeaders}
        help="Force-override the avatar. Overrides whatever they set in their own profile." />

      <Field label="Bio" help="Displayed on the public Team page card.">
        <textarea value={form.bio || ''} onChange={(e) => updateField('bio', e.target.value)} rows={3} className={`${INPUT_CLS} resize-none`} data-testid="edit-staff-bio" />
      </Field>

      {/* Socials — force override, per-platform */}
      <Field label={<span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Social Links (force override)</span>}
        help="Override any social link they set on their own profile. Leave blank to hide that platform.">
        <div className="grid md:grid-cols-2 gap-2">
          {Object.entries(SOCIAL_ICONS).map(([platform, Icon]) => (
            <div key={platform} className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-white/[0.03] border border-white/[0.08] flex items-center justify-center flex-shrink-0">
                <Icon className="w-3.5 h-3.5 text-white/50" />
              </div>
              <input value={form.socials[platform] || ''} onChange={(e) => updateSocial(platform, e.target.value)}
                className={INPUT_CLS} placeholder={platform} />
            </div>
          ))}
        </div>
      </Field>

      {/* Tags */}
      <Field label="Tags" help="Show role-specific badges on their card (e.g. DMCA Officer).">
        <div className="space-y-2">
          {form.tags.map((t, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input value={t.label} onChange={(e) => updateTag(idx, 'label', e.target.value)}
                placeholder="Tag label (e.g. DMCA Officer)" className={`${INPUT_CLS} flex-1`} />
              <select value={t.color || 'cyan'} onChange={(e) => updateTag(idx, 'color', e.target.value)}
                className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2.5 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/40">
                {TAG_COLORS.map(c => <option key={c.value} value={c.value} className="bg-[#0A0A12]">{c.label}</option>)}
              </select>
              <button onClick={() => removeTag(idx)} className="w-9 h-9 rounded-lg bg-white/[0.03] border border-white/[0.08] hover:bg-red-500/10 hover:border-red-500/25 text-white/40 hover:text-red-300 flex items-center justify-center">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={addTag} data-testid="add-staff-tag"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] tracking-[0.1em] uppercase bg-white/[0.03] border border-white/[0.08] hover:bg-[#00E5FF]/[0.08] hover:border-[#00E5FF]/25 text-white/60 hover:text-[#00E5FF]">
            <Plus className="w-3 h-3" /> Add tag
          </button>
        </div>
      </Field>

      <VisibilityToggle hidden={!!form.hidden} onToggle={() => updateField('hidden', !form.hidden)} />

      <ModalFooter onCancel={onClose} onSave={() => onSave(form)} saving={saving} testid="save-staff-btn" />
    </ModalShell>
  );
};

// ─── Shared shell + fields ───
const ModalShell = ({ title, name, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={onClose} data-testid="team-mgmt-modal">
    <div onClick={(e) => e.stopPropagation()}
      className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0A0A12] border border-white/10 rounded-2xl"
      style={{ boxShadow: '0 0 80px rgba(0,229,255,0.08)' }}>
      <div className="sticky top-0 bg-[#0A0A12]/95 backdrop-blur-md border-b border-white/[0.06] px-6 py-4 flex items-center justify-between z-10">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-medium">Editing {title.toLowerCase()}</p>
          <h2 className="text-lg font-medium text-white tracking-tight">{name}</h2>
        </div>
        <button onClick={onClose} data-testid="close-team-mgmt-modal"
          className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center text-white/60 hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  </div>
);

const ModalFooter = ({ onCancel, onSave, saving, testid }) => (
  <div className="flex gap-2 justify-end pt-3 border-t border-white/[0.06]">
    <Button variant="ghost" onClick={onCancel} className="text-white/60 hover:text-white">Cancel</Button>
    <Button onClick={onSave} disabled={saving} data-testid={testid}
      className="bg-gradient-to-br from-[#00E5FF] to-[#8A2BE2] text-black font-medium px-5 rounded-full text-[11px] tracking-[0.1em] uppercase">
      {saving ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5 mr-2" /> Save changes</>}
    </Button>
  </div>
);

const AvatarField = ({ value, onChange, help, staffId, getAuthHeaders, onUploaded }) => {
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB'); return;
    }
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const fd = new FormData();
      let url;
      if (staffId) {
        // Fast path — one-shot upload + override in a single call
        fd.append('photo', file);
        const res = await axios.post(
          `${BACKEND_URL}/api/admin/staff-profile-overrides/${staffId}/photo`,
          fd,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
        url = res.data.avatar_url;
      } else {
        // Generic upload — used by exec avatars where no staff_id exists yet
        fd.append('avatar', file);
        const res = await axios.post(
          `${BACKEND_URL}/api/admin/upload-avatar`,
          fd,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
        url = res.data.avatar_url;
      }
      const full = url?.startsWith('http') ? url : `${BACKEND_URL}${url}`;
      onChange(full);
      toast.success('Photo updated');
      onUploaded && onUploaded(full);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field label="Avatar" help={help || "Upload a photo — it's mirrored to storage + local disk so it never disappears."}>
      <div className="flex gap-3 items-start">
        <div className="w-16 h-16 rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden flex-shrink-0 relative group">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><ImagePlus className="w-5 h-5 text-white/20" /></div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid={staffId ? `avatar-upload-${staffId}` : 'avatar-upload-input'}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-9 px-3 rounded-lg text-[11px] tracking-[0.08em] uppercase bg-gradient-to-br from-[#00E5FF]/15 to-[#8A2BE2]/15 text-cyan-200 border border-cyan-500/25 hover:border-cyan-500/50 hover:from-[#00E5FF]/25 hover:to-[#8A2BE2]/25"
            >
              {uploading ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Uploading</> : <><ImagePlus className="w-3 h-3 mr-1.5" /> Choose photo</>}
            </Button>
            {value && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => onChange('')}
                className="h-9 px-3 rounded-lg text-[11px] tracking-[0.08em] uppercase text-white/40 hover:text-red-300 hover:bg-red-500/[0.06]"
              >
                <X className="w-3 h-3 mr-1.5" /> Remove
              </Button>
            )}
          </div>
          <input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={INPUT_CLS}
            placeholder="…or paste a direct URL"
          />
        </div>
      </div>
    </Field>
  );
};

const VisibilityToggle = ({ hidden, onToggle }) => (
  <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.015]">
    <label className="flex items-center gap-2.5 cursor-pointer group">
      <button type="button" onClick={onToggle} data-testid="toggle-hidden"
        className={`relative w-9 h-5 rounded-full transition-colors ${hidden ? 'bg-red-500/40' : 'bg-emerald-500/30'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${hidden ? 'left-4 bg-red-400' : 'left-0.5 bg-emerald-400'}`} />
      </button>
      <span className="text-xs text-white/70 flex items-center gap-1.5">
        {hidden ? <><EyeOff className="w-3 h-3 text-red-300" /> Hidden from public Team page</> : <><Eye className="w-3 h-3 text-emerald-300" /> Visible on public Team page</>}
      </span>
    </label>
  </div>
);

const EmailField = ({ email, show, onEmail, onShow }) => (
  <div className="p-4 rounded-xl border border-white/[0.06] bg-white/[0.015]">
    <div className="flex items-center gap-1.5 mb-3">
      <Mail className="w-3.5 h-3.5 text-white/40" />
      <p className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium">Contact email</p>
    </div>
    <input value={email || ''} onChange={(e) => onEmail(e.target.value)} className={INPUT_CLS} placeholder="name@starnightholdings.uk" type="email" />
    <label className="flex items-center gap-2.5 mt-3 cursor-pointer group">
      <button type="button" onClick={onShow} data-testid="toggle-show-email"
        className={`relative w-9 h-5 rounded-full transition-colors ${show ? 'bg-[#00E5FF]/30' : 'bg-white/[0.06]'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${show ? 'left-4 bg-[#00E5FF]' : 'left-0.5 bg-white/40'}`} />
      </button>
      <span className="text-xs text-white/60 group-hover:text-white/80 flex items-center gap-1.5">
        {show ? <Eye className="w-3 h-3 text-[#00E5FF]" /> : <EyeOff className="w-3 h-3 text-amber-300" />}
        {show ? 'Show email publicly on Team page' : 'Hide email from public (recommended)'}
      </span>
    </label>
  </div>
);

const Field = ({ label, children, help }) => (
  <div>
    <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium block mb-1.5">{label}</label>
    {children}
    {help && <p className="text-[10px] text-white/30 mt-1">{help}</p>}
  </div>
);

const ArrayField = ({ label, items, placeholder, onChange, onAdd, onRemove }) => (
  <div>
    <label className="text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium block mb-2">{label}</label>
    <div className="space-y-1.5">
      {items.map((val, i) => (
        <div key={i} className="flex gap-1.5">
          <input value={val} onChange={(e) => onChange(i, e.target.value)} className={INPUT_CLS} placeholder={placeholder} />
          {items.length > 1 && (
            <button onClick={() => onRemove(i)} className="w-9 h-9 rounded-lg bg-red-500/[0.05] border border-red-500/15 hover:bg-red-500/[0.12] text-red-300 flex items-center justify-center transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
    </div>
    <button onClick={onAdd} className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.1em] uppercase bg-white/[0.03] hover:bg-white/[0.06] text-white/50 hover:text-white border border-white/[0.06] transition-colors">
      <Plus className="w-3 h-3" /> Add item
    </button>
  </div>
);

export default TeamManagementPage;
